import { useContext, useState } from "react";

import { Box, Button, Heading, HStack, Stack } from "@chakra-ui/react";
import { ColumnFiltersState, createColumnHelper } from "@tanstack/react-table";

import { ZoneType } from "@/codegen/openapi2";
import { toaster } from "@/codegen/ui/toaster";
import { TableLayout } from "@/components/2/layouts/table";
import { LegalEntityName } from "@/components/2/legal-entity";
import { LegalEntityContext } from "@/components/2/legal-entity-context";
import { usePermerRequirements } from "@/hooks/useHasPermission";
import {
  useGetZonesGeographiesZonesGet,
  useUpdateZoneGeographiesZonesZoneUuidPut,
} from "@/services/2/react-query/hooks";

import {
  AircallNumberIdCell,
  AircallPhoneCell,
  CustomerServiceCell,
  RegionSelectorCell,
  ZoneNameCell,
} from "./cells";
import { ZoneCreateModal } from "./zone-create-modal";
import { validateZoneField, ZoneFieldValidators } from "./zone-creation-schema";
import { ZoneFilters } from "./zone-filters";

const columnHelper = createColumnHelper<ZoneType>();

export const Zones = () => {
  const legalEntityContext = useContext(LegalEntityContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const permissions = usePermerRequirements();
  const canEditZones = permissions.hasDsitGeographyZonesEdit();

  const queryParams = {
    legal_entity_uuids: legalEntityContext ? [legalEntityContext] : null,
  };

  const { data, refetch } = useGetZonesGeographiesZonesGet(
    { query: queryParams },
    ["zones", queryParams],
    { placeholderData: [] }
  );

  const { mutate: updateZone } = useUpdateZoneGeographiesZonesZoneUuidPut();

  const handleUpdate = (zone: ZoneType, field: string, value: string) => {
    // Validate the field using Zod
    const validation = validateZoneField(field as keyof typeof ZoneFieldValidators, value);

    if (!validation.success) {
      toaster.create({
        title: "Saisie invalide",
        description: validation.error,
        type: "error",
      });
      return;
    }

    // Prepare the update data with validated/transformed value
    const updateData = {
      ...zone,
      [field]: validation.data,
    };

    updateZone(
      {
        path: { zone_uuid: zone.uuid },
        body: updateData,
      },
      {
        onSuccess: updatedZone => {
          toaster.create({
            title: "Zone mise à jour avec succès",
            type: "success",
          });
          // Update with backend-formatted data
          Object.assign(zone, updatedZone);
        },
        onError: () => {
          toaster.create({
            title: "Erreur lors de la mise à jour de la zone",
            type: "error",
          });
        },
      }
    );
  };

  const columns = [
    columnHelper.accessor("legal_entity_uuid", {
      header: "Entité",
      cell: ({ cell }) => <LegalEntityName legalEntityUuid={cell.getValue()} />,
      minSize: 150,
      filterFn: "arrIncludesSome",
    }),
    columnHelper.display({
      header: "Nom",
      cell: ({ row }) => (
        <ZoneNameCell zone={row.original} onUpdate={canEditZones ? handleUpdate : undefined} />
      ),
      minSize: 200,
    }),
    columnHelper.accessor("region_uuid", {
      header: "Région",
      cell: ({ row }) => (
        <RegionSelectorCell
          zone={row.original}
          onUpdate={canEditZones ? handleUpdate : undefined}
        />
      ),
      minSize: 250,
      filterFn: "arrIncludesSome",
    }),
    columnHelper.display({
      header: "Service Client ID",
      cell: ({ row }) => (
        <CustomerServiceCell
          zone={row.original}
          onUpdate={canEditZones ? handleUpdate : undefined}
        />
      ),
      minSize: 150,
    }),
    columnHelper.display({
      header: "Aircall Coordo",
      cell: ({ row }) => (
        <AircallPhoneCell
          phoneNumber={row.original.aircall_phone_number}
          onUpdate={
            canEditZones
              ? (value: string) => handleUpdate(row.original, "aircall_phone_number", value)
              : undefined
          }
        />
      ),
      minSize: 200,
    }),
    columnHelper.display({
      header: "ID Numéro Coordo",
      cell: ({ row }) => (
        <AircallNumberIdCell
          id={row.original.aircall_main_number_id}
          onUpdate={
            canEditZones
              ? (value: string) => handleUpdate(row.original, "aircall_main_number_id", value)
              : undefined
          }
        />
      ),
      minSize: 180,
    }),
    columnHelper.display({
      header: "Aircall RH",
      cell: ({ row }) => (
        <AircallPhoneCell
          phoneNumber={row.original.aircall_rh_number}
          onUpdate={
            canEditZones
              ? (value: string) => handleUpdate(row.original, "aircall_rh_number", value)
              : undefined
          }
        />
      ),
      minSize: 200,
    }),
    columnHelper.display({
      header: "ID Numéro RH",
      cell: ({ row }) => (
        <AircallNumberIdCell
          id={row.original.aircall_main_rh_number_id}
          onUpdate={
            canEditZones
              ? (value: string) => handleUpdate(row.original, "aircall_main_rh_number_id", value)
              : undefined
          }
        />
      ),
      minSize: 180,
    }),
  ];

  return (
    <>
      <Stack>
        <Box p={8} backgroundColor={"gray.100"}>
          <HStack justify="space-between" align="center">
            <Heading size="2xl">Zones</Heading>
            {canEditZones && (
              <Button colorScheme="blue" onClick={() => setIsCreateModalOpen(true)}>
                Créer une nouvelle zone
              </Button>
            )}
          </HStack>
        </Box>

        <Stack p={8} gap={4}>
          <ZoneFilters columnFilters={columnFilters} setColumnFilters={setColumnFilters} />
          <TableLayout
            autoResetPageIndex
            data={data as ZoneType[]}
            columns={columns}
            withPagination
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
          />
        </Stack>
      </Stack>

      <ZoneCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => refetch()}
      />
    </>
  );
};
